import {useSearchParams} from 'next/navigation'
import {useMemo, useState} from 'react'

import {QUERY_PARAM_PAGINATION_KEY} from '@/components/common/content/pagination/constants'
import {buildPaginationUrl, paginate} from '@/components/common/content/pagination/paginate'
import {useRouter} from '@/utils/hooks/useRouter'

export const usePagination = <T>(items: T[], perPage: number) => {
  const [currentPage, setCurrentPage] = useState(1)
  const {
    items: currentItems,
    currentPage: clampedCurrentPage,
    numberOfPages,
  } = paginate(items, perPage, String(currentPage))

  return {
    currentPage: clampedCurrentPage,
    setCurrentPage,
    numberOfPages,
    currentItems,
  }
}

export const useSearchParamsPagination = (
  perPage: number,
  paginationKey: string = QUERY_PARAM_PAGINATION_KEY,
) => {
  const router = useRouter()
  const pathname = router.pathname
  const searchParams = useSearchParams()
  const replace = router.replace

  const currentPage = useMemo(() => {
    const pageFromParam = searchParams?.get(paginationKey)
    return pageFromParam ? parseInt(pageFromParam, 10) : 1
  }, [searchParams, paginationKey])

  const offset = useMemo(() => (currentPage - 1) * perPage, [currentPage, perPage])

  const setCurrentPage = async (page: number) => {
    const url = buildPaginationUrl(
      pathname,
      new URLSearchParams(searchParams?.toString()),
      page,
      paginationKey,
    )

    await replace?.(url, {scroll: false})
  }

  return {
    currentPage,
    setCurrentPage,
    offset,
  }
}
